import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { CountsValidatedSummaryBo } from "@/types/api/api-types";
import { FilterTabs, type FilterTabOption } from "@/components/ui/customs/filter-tabs";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import Paths from "@/constants/paths";
import type { DeliveryStatusGroup } from "./useValidatedExpeditionsFilters.reducer";

const INACTIVE_TAB = "text-gray-500 hover:bg-gray-100";
const INACTIVE_BADGE = "bg-gray-500/10 text-gray-500";

const useStatusOptions = (): FilterTabOption[] => {
  const { t } = useTranslation("boExpeditions");
  return useMemo(
    () => [
      {
        label: t("statusBox.ongoing"),
        value: "ongoing",
        width: "w-28",
        activeClassName: "bg-yellow-500 text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
      {
        label: t("statusBox.delivered"),
        value: "delivered",
        width: "w-24",
        activeClassName: "bg-green-600 text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
      {
        label: t("statusBox.failed"),
        value: "failed",
        width: "w-24",
        activeClassName: "bg-red-500 text-white",
        inactiveClassName: INACTIVE_TAB,
        badgeActiveClassName: "bg-white/20 text-white",
        badgeInactiveClassName: INACTIVE_BADGE,
      },
    ],
    [t],
  );
};

type Props = {
  deliveryStatusGroup: DeliveryStatusGroup;
  onDeliveryStatusGroupChange: (group: DeliveryStatusGroup) => void;
  counts?: CountsValidatedSummaryBo["quickFilters"];
  deletedCount?: number;
  isSuperadmin?: boolean;
};

export const ValidatedExpeditionsStatusBox = ({
  deliveryStatusGroup,
  onDeliveryStatusGroupChange,
  counts,
  deletedCount,
  isSuperadmin,
}: Props) => {
  const { t } = useTranslation("boExpeditions");
  const navigate = useNavigate();
  const statusOptions = useStatusOptions();
  const countsMap = new Map<string | null, number | undefined>([
    ["ongoing", counts?.ongoing],
    ["delivered", counts?.delivered],
    ["failed", counts?.failed],
  ]);

  return (
    <div className="my-2 flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <FilterTabs
        options={statusOptions}
        value={deliveryStatusGroup}
        onChange={(v) => onDeliveryStatusGroupChange(v as DeliveryStatusGroup)}
        counts={countsMap}
        className="w-full flex-col border-none !bg-transparent !shadow-none sm:w-fit sm:flex-row"
      />
      {isSuperadmin && (
        <button
          type="button"
          onClick={() => navigate(Paths.bo.deletedExpeditionsV2)}
          className={cn(
            "flex items-center gap-2 rounded-sm px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
            INACTIVE_TAB,
          )}
        >
          <Trash2 className="size-4" />
          <span>{t("statusBox.trash")}</span>
          {deletedCount !== undefined && (
            <span
              className={cn(
                "text-md flex items-center justify-center rounded-full px-1.5 font-semibold",
                INACTIVE_BADGE,
              )}
            >
              {deletedCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
};
